import { useRef, useState } from 'react';
import { Upload, X, FileText } from 'lucide-react';
import { cn } from "@/lib/utils";
import { handleFileUpload } from '@/utils/files/upload';
import { UPLOAD_CONFIG } from '@/config';

interface FileUploadProps {
  value: string;
  onChange: (value: string) => void;
  type: 'image' | 'pdf';
  label: string;
  onError?: (error: string) => void;
}

export function FileUpload({ 
  value, 
  onChange, 
  type,
  label,
  onError
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);

  const maxSize = type === 'image' ? UPLOAD_CONFIG.MAX_IMAGE_SIZE : UPLOAD_CONFIG.MAX_PDF_SIZE;
  const accept = type === 'image' 
    ? UPLOAD_CONFIG.ACCEPTED_IMAGE_TYPES.join(',') 
    : UPLOAD_CONFIG.ACCEPTED_PDF_TYPES.join(',');

  const handleFile = async (file: File) => {
    setIsUploading(true);
    try {
      const url = await handleFileUpload(file, maxSize, onError);
      if (url) {
        onChange(url);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  return (
    <div className="space-y-4">
      {value ? (
        <div className="relative">
          {type === 'image' ? (
            <div className="relative w-20 h-20 rounded-lg overflow-hidden">
              <img 
                src={value}
                alt="Preview"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-1 right-1 p-1 bg-white rounded-lg shadow-sm hover:bg-gray-100"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <FileText className="h-5 w-5 text-gray-500" />
              <div className="flex-1 truncate text-sm">تم اختيار الملف</div>
              <button
                type="button"
                onClick={() => onChange('')}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div 
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setIsDragActive(true)}
          onDragLeave={() => setIsDragActive(false)}
          className={cn(
            "relative border-2 border-dashed rounded-lg transition-all cursor-pointer",
            "hover:border-primary/50 hover:bg-gray-50",
            isDragActive ? "border-primary bg-primary/5" : "border-gray-200 bg-white",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          )}
        >
          <div className="px-6 py-8">
            <div className="flex flex-col items-center gap-3">
              <div className={cn(
                "p-3 rounded-lg transition-colors",
                isDragActive ? "bg-primary/10" : "bg-gray-100"
              )}>
                <Upload className={cn(
                  "h-6 w-6",
                  isDragActive ? "text-primary" : "text-gray-400"
                )} />
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  {isUploading ? 'جاري الرفع...' : (
                    isDragActive ? 'أفلت الملف هنا' : (
                      <>
                        <span className="text-primary">اضغط للاختيار</span>
                        {' أو اسحب وأفلت '}
                        {label}
                      </>
                    )
                  )}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  الحد الأقصى للحجم: {Math.round(maxSize / (1024 * 1024))}MB
                </p>
              </div>
            </div>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleFileInput}
            className="hidden"
            disabled={isUploading}
          />
        </div>
      )}
    </div>
  );
}